import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';
import { showToast } from '../toast';
import { store } from '../state';
import type { Ticker } from '../state';

interface SignalComponent { score: number; factors: string[]; }
interface TickerSignal {
  ticker:     string;
  signal:     'BUY' | 'HOLD' | 'SELL';
  score:      number;
  rawScore:   number;
  components: { momentum: SignalComponent; sentiment: SignalComponent; fundamentals: SignalComponent; };
  data: {
    price:          number;
    sma20:          number;
    sma50:          number;
    rsi:            number;
    change20d:      number;
    pe:             number | null;
    revenueGrowth:  number | null;
    profitMargin:   number | null;
    sentimentScore: number;
    sentimentLabel: string;
  };
}
interface SignalModelResponse { signals: TickerSignal[]; cachedAt: string; }

const SIG_COLOR = { BUY: 'var(--green)', HOLD: 'var(--warn)', SELL: 'var(--red)' };
const SIG_BG    = { BUY: 'rgba(46,213,115,0.1)', HOLD: 'rgba(255,193,7,0.1)', SELL: 'rgba(255,71,87,0.1)' };

function scoreBar(score: number, color: string, width = 120): string {
  const pct = Math.round(score);
  return `<div style="width:${width}px;height:6px;background:var(--bg3);border-radius:3px;overflow:hidden;">
    <div style="width:${pct}%;height:100%;background:${color};border-radius:3px;transition:width 0.4s;"></div>
  </div>`;
}

function componentCell(comp: SignalComponent, max: number): string {
  const norm = Math.round(((comp.score + max) / (max * 2)) * 100);
  const col  = norm >= 60 ? 'var(--green)' : norm <= 40 ? 'var(--red)' : 'var(--warn)';
  const sign = comp.score > 0 ? '+' : '';
  return `<div style="text-align:center;">
    <div style="font-family:var(--mono);font-size:12px;font-weight:700;color:${col};">${sign}${comp.score}</div>
    ${scoreBar(norm, col, 70)}
  </div>`;
}

export class SignalModelTab implements Tab {
  readonly id = 'signal-model';
  private currentTickers: string[] = [];

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    this.el('sm-analyze-btn').addEventListener('click', () => this.analyzeInput());
    this.el('sm-portfolio-btn').addEventListener('click', () => this.loadPortfolio());
    this.el('sm-refresh-btn').addEventListener('click', () => { if (this.currentTickers.length) this.load(this.currentTickers); });
    (this.el('sm-ticker-input') as HTMLInputElement)
      .addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter') this.analyzeInput(); });

    // Click on chart link
    this.el('sm-body').addEventListener('click', (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-sm-ticker]');
      if (btn) {
        (window as any).showTab?.('charts');
        (window as any).loadChartTicker?.(btn.dataset.smTicker);
      }
      // Expand details row
      const row = (e.target as HTMLElement).closest<HTMLElement>('[data-sm-expand]');
      if (row) {
        const detail = document.getElementById('sm-detail-' + row.dataset.smExpand);
        if (detail) detail.style.display = detail.style.display === 'none' ? 'table-row' : 'none';
      }
    });
  }

  render(): void {
    if (!this.currentTickers.length) this.loadPortfolio();
  }

  private analyzeInput(): void {
    const raw = (this.el('sm-ticker-input') as HTMLInputElement).value.trim().toUpperCase();
    if (!raw) return;
    const tickers = raw.split(',').map(t => t.trim()).filter(Boolean).slice(0, 10);
    this.load(tickers);
  }

  private loadPortfolio(): void {
    const tickers = (store.get('tickers') as Ticker[])
      .map(h => (h.Ticker ?? '').toUpperCase().trim())
      .filter(Boolean).slice(0, 10);
    if (!tickers.length) {
      showToast('No portfolio tickers — sync Holdings or enter tickers manually', 'warn');
      return;
    }
    (this.el('sm-ticker-input') as HTMLInputElement).value = tickers.join(', ');
    this.load(tickers);
  }

  private async load(tickers: string[]): Promise<void> {
    this.currentTickers = tickers;
    const alpacaKey    = localStorage.getItem('meridian_alpaca_key')    ?? '';
    const alpacaSecret = localStorage.getItem('meridian_alpaca_secret') ?? '';

    if (!alpacaKey || !alpacaSecret) {
      this.el('sm-body').innerHTML = `
        <div class="empty-state">
          <div style="font-size:22px;margin-bottom:8px;">⚙</div>
          <div style="font-family:var(--mono);font-size:12px;color:var(--warn);">Alpaca API keys required</div>
          <div style="font-size:11px;color:var(--text3);margin-top:6px;">Open <kbd style="background:var(--bg3);border:1px solid var(--border);padding:1px 5px;border-radius:2px;font-family:var(--mono);">Ctrl+Shift+,</kbd> to configure</div>
        </div>`;
      return;
    }

    this.el('sm-body').innerHTML = `
      <div style="padding:20px;color:var(--text3);font-family:var(--mono);font-size:12px;">
        Analyzing ${tickers.length} ticker${tickers.length > 1 ? 's' : ''}… momentum · sentiment · fundamentals
      </div>`;

    try {
      const data = await workerFetch<SignalModelResponse>('/api/signal-model', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers, alpacaKey, alpacaSecret }),
        signal: AbortSignal.timeout(90000),
      });
      this.el('sm-updated').textContent = `Cached ${timeAgo(data.cachedAt)}`;
      this.renderTable(data.signals);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.el('sm-body').innerHTML =
        `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private renderTable(signals: TickerSignal[]): void {
    if (!signals.length) { this.el('sm-body').innerHTML = '<div style="padding:20px;color:var(--text3);">No results.</div>'; return; }

    const rows = signals.map(s => {
      const sigCol = SIG_COLOR[s.signal];
      const sigBg  = SIG_BG[s.signal];
      const rsiCol = s.data.rsi > 70 ? 'var(--warn)' : s.data.rsi < 30 ? 'var(--red)' : 'var(--text)';
      const chgCol = s.data.change20d >= 0 ? 'var(--green)' : 'var(--red)';
      const factors = [...s.components.momentum.factors, ...s.components.sentiment.factors, ...s.components.fundamentals.factors];

      return `
        <tr data-sm-expand="${esc(s.ticker)}" style="cursor:pointer;border-bottom:1px solid var(--border);">
          <td style="padding:12px 14px;">
            <div style="display:flex;align-items:center;gap:5px;">
              <button data-sm-ticker="${esc(s.ticker)}"
                style="background:var(--bg3);border:1px solid var(--border);color:var(--accent);font-family:var(--mono);font-size:12px;font-weight:700;padding:3px 8px;cursor:pointer;border-radius:2px;">
                ${esc(s.ticker)}
              </button>
              <button data-sm-ticker="${esc(s.ticker)}"
                title="Open chart"
                style="background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.2);color:var(--accent);font-family:var(--mono);font-size:10px;padding:2px 6px;cursor:pointer;border-radius:2px;">📈</button>
            </div>
          </td>
          <td style="padding:12px 14px;">
            <span style="background:${sigBg};border:1px solid ${sigCol};color:${sigCol};font-family:var(--mono);font-size:11px;font-weight:700;padding:3px 10px;border-radius:2px;letter-spacing:0.1em;">
              ${esc(s.signal)}
            </span>
          </td>
          <td style="padding:12px 14px;">
            <div style="display:flex;flex-direction:column;gap:4px;">
              <span style="font-family:var(--mono);font-size:13px;font-weight:700;color:${sigCol};">${s.score}</span>
              ${scoreBar(s.score, sigCol, 80)}
            </div>
          </td>
          <td style="padding:12px 14px;">${componentCell(s.components.momentum, 40)}</td>
          <td style="padding:12px 14px;">${componentCell(s.components.sentiment, 30)}</td>
          <td style="padding:12px 14px;">${componentCell(s.components.fundamentals, 30)}</td>
          <td style="padding:12px 14px;font-family:var(--mono);font-size:12px;color:${rsiCol};">${s.data.rsi}</td>
          <td style="padding:12px 14px;font-family:var(--mono);font-size:12px;color:${chgCol};">${s.data.change20d >= 0 ? '+' : ''}${s.data.change20d}%</td>
          <td style="padding:12px 14px;font-family:var(--mono);font-size:11px;color:var(--text3);">${s.data.pe != null ? s.data.pe + '×' : '—'}</td>
          <td style="padding:12px 14px;font-size:10px;color:var(--text3);">▼</td>
        </tr>
        <tr id="sm-detail-${esc(s.ticker)}" style="display:none;background:var(--bg2);">
          <td colspan="10" style="padding:12px 20px;">
            <div style="display:flex;gap:24px;flex-wrap:wrap;">
              ${['Momentum','Sentiment','Fundamentals'].map((label, i) => {
                const comp = [s.components.momentum, s.components.sentiment, s.components.fundamentals][i];
                return `<div style="min-width:180px;">
                  <div style="font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;margin-bottom:6px;">${label}</div>
                  ${comp.factors.map(f => `<div style="font-size:11px;color:var(--text2);margin-bottom:3px;">• ${esc(f)}</div>`).join('')}
                  ${!comp.factors.length ? '<div style="font-size:11px;color:var(--text3);">No data</div>' : ''}
                </div>`;
              }).join('')}
            </div>
          </td>
        </tr>`;
    }).join('');

    this.el('sm-body').innerHTML = `
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="border-bottom:2px solid var(--border);">
            <th style="padding:8px 14px;text-align:left;font-size:10px;letter-spacing:0.12em;color:var(--text3);text-transform:uppercase;">Ticker</th>
            <th style="padding:8px 14px;text-align:left;font-size:10px;letter-spacing:0.12em;color:var(--text3);text-transform:uppercase;">Signal</th>
            <th style="padding:8px 14px;text-align:left;font-size:10px;letter-spacing:0.12em;color:var(--text3);text-transform:uppercase;">Score /100</th>
            <th style="padding:8px 14px;text-align:left;font-size:10px;letter-spacing:0.12em;color:var(--text3);text-transform:uppercase;">Momentum</th>
            <th style="padding:8px 14px;text-align:left;font-size:10px;letter-spacing:0.12em;color:var(--text3);text-transform:uppercase;">Sentiment</th>
            <th style="padding:8px 14px;text-align:left;font-size:10px;letter-spacing:0.12em;color:var(--text3);text-transform:uppercase;">Fundamentals</th>
            <th style="padding:8px 14px;text-align:left;font-size:10px;letter-spacing:0.12em;color:var(--text3);text-transform:uppercase;">RSI</th>
            <th style="padding:8px 14px;text-align:left;font-size:10px;letter-spacing:0.12em;color:var(--text3);text-transform:uppercase;">20d Chg</th>
            <th style="padding:8px 14px;text-align:left;font-size:10px;letter-spacing:0.12em;color:var(--text3);text-transform:uppercase;">P/E</th>
            <th style="padding:8px 14px;"></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div style="font-size:10px;color:var(--text3);text-align:right;padding:8px 2px;margin-top:4px;">
        Score = momentum (40%) + sentiment (30%) + fundamentals (30%) · click row for factor breakdown
      </div>`;
  }
}
